/* eslint-disable max-lines */
import { ArcLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import {
  bbox, booleanPointInPolygon, greatCircle, point,
} from '@turf/turf';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import MapComponent, {
  MapLegend,
} from '@App/components/Map/Map';
import Question2Form from '@App/pages/Tablet/Question2/Question2Form';
import attractionData from '@App/components/Map/data/attraction-data';
import flandersBrusselsRegion from '@App/components/Map/data/flanders-brussels-region';
import postalLocalities from '@App/pages/Tablet/Introduction/data/belgian-postal-localities.json';
import {
  getCount, getParticipantResidence, makeMunicipalityData,
} from '../question.utils';

const QUESTION_ID = 'question2';
const ARC_WIDTH_PER_PERSON = 1.5;
const MAX_ARC_WIDTH = 8;
const MAX_STAY_RADIUS = 6000;
const STAY_RADIUS_PER_PERSON = 1200;
const LABEL_ELEVATION = 2400;
function isKnownFlandersBrusselsResidence(residenceName) {
  const normalizedName = normalizeAreaName(residenceName);

  return postalLocalities.some(locality => (
    normalizeAreaName(locality.name) === normalizedName
      && ['Vlaanderen', 'Bruxelles-Capitale'].includes(locality.region)
  ));
}

function isOutsideFlandersBrussels(coordinates, residenceName) {
  if (residenceName && !isKnownFlandersBrusselsResidence(residenceName)) return true;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return true;

  return !flandersBrusselsRegion.some(region => (
    booleanPointInPolygon(point(coordinates), region)
  ));
}

function normalizeAreaName(value) {
  return value?.replace(/\s+\([^)]*\)$/, '').normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').trim()
    .toLocaleLowerCase('nl-BE');
}
function buildQuestionData(
  answerCount,
  lastAnswer,
  participantAnswers,
  { showAllData = true } = {},
) {
  const municipalities = makeMunicipalityData(attractionData.features);
  const visibleParticipants = showAllData || !lastAnswer?.participantId
    ? participantAnswers
    : participantAnswers.filter(participant => participant.id === lastAnswer.participantId);
  const focusedParticipantId = !showAllData ? lastAnswer?.participantId : null;
  const focusedAnswer = focusedParticipantId && lastAnswer.answer
    ? {
      ...lastAnswer.answer,
      residenceCoordinates: lastAnswer.answer.residenceCoordinates
        || lastAnswer.answer.startCoordinates,
    }
    : null;
  const focusedParticipants = visibleParticipants.map((participant) => {
    if (!focusedAnswer || participant.id !== focusedParticipantId) return participant;

    return {
      ...participant,
      answers: { ...participant.answers, question2: focusedAnswer },
    };
  });
  const focusedParticipant = focusedAnswer
    && !visibleParticipants.some(participant => participant.id === focusedParticipantId)
    ? [{ id: focusedParticipantId, answers: { question2: focusedAnswer } }]
    : [];

  return [...focusedParticipants, ...focusedParticipant].map((participant) => {
    const answer = participant.answers?.question2;
    const municipality = municipalities.find(item => (
      item.name === answer?.value || item.name === answer?.label
    ));
    const residence = getParticipantResidence(participant);
    const focusedResidenceCoordinates = participant.id === focusedParticipantId && focusedAnswer
      ? normalizeCoordinates(focusedAnswer.startCoordinates || focusedAnswer.residenceCoordinates)
      : null;
    const resolvedResidence = focusedResidenceCoordinates
      ? { ...residence, coordinates: focusedResidenceCoordinates }
      : residence;

    if (!municipality) return null;
    const residenceName = resolvedResidence.name || answer.residence || 'Vertrek';
    const knownResidenceName = resolvedResidence.name || answer.residence;
    const destinationName = municipality.name;

    return {
      coordinates: municipality.coordinates,
      count: getCount(answerCount, [
        municipality.name,
        `municipality:${municipality.name}`,
      ]),
      residence: resolvedResidence.coordinates,
      residenceName,
      destinationName,
      isOutsideFlandersBrussels: isOutsideFlandersBrussels(
        residence.coordinates,
        knownResidenceName,
      ),
      isStaying: normalizeAreaName(residenceName) === normalizeAreaName(destinationName),
      participantId: participant.id,
    };
  }).filter(Boolean);
}
function getNetFlows(data) {
  const flowGroups = data.reduce((groups, item) => {
    if (item.isStaying || item.isOutsideFlandersBrussels) return groups;
    const origin = normalizeAreaName(item.residenceName);
    const destination = normalizeAreaName(item.destinationName);
    if (!origin || !destination || origin === destination) return groups;

    const [first, second] = [origin, destination].sort();
    const key = `${first}|${second}`;
    const group = groups.get(key) || {
      forward: { count: 0, item: null },
      reverse: { count: 0, item: null },
    };
    const direction = origin === first ? group.forward : group.reverse;
    direction.count += 1;
    direction.item = direction.item || item;
    groups.set(key, group);
    return groups;
  }, new Map());

  return [...flowGroups.values()].map(({ forward, reverse }) => {
    const difference = forward.count - reverse.count;
    if (difference === 0) return null;

    const flow = difference > 0 ? forward : reverse;
    return { ...flow.item, flowCount: Math.abs(difference) };
  }).filter(Boolean);
}
function getQuestionLayers({ data, lastAnswer, options = {} }) {
  const localData = data.filter(item => !item.isOutsideFlandersBrussels);
  const flowData = getNetFlows(localData);
  const stayLocations = localData.reduce((locations, item) => {
    if (!item.isStaying) return locations;
    const current = locations.get(item.destinationName) || {
      coordinates: item.coordinates,
      count: 0,
    };
    locations.set(item.destinationName, { ...current, count: current.count + 1 });
    return locations;
  }, new Map());
  const focusedOutsideDestinations = data
    .filter(item => (
      lastAnswer?.questionId === QUESTION_ID
      && lastAnswer.participantId
      && item.participantId === lastAnswer.participantId
      && item.isOutsideFlandersBrussels
    ))
    .reduce((destinations, item) => {
      const current = destinations.get(item.destinationName) || {
        coordinates: item.coordinates,
        count: 0,
      };
      destinations.set(item.destinationName, {
        ...current,
        count: current.count + 1,
      });
      return destinations;
    }, new Map());
  const labels = localData.flatMap(item => ([
    {
      coordinates: item.residence,
      name: item.residenceName,
    },
    {
      coordinates: item.coordinates,
      name: item.destinationName,
    },
  ]));
  const symbols = [
    ...stayLocations.values(),
    ...flowData,
  ];
  return [new ArcLayer({
    id: 'question-2-spider',
    data: flowData,
    getSourceColor: [220, 80, 80, 220],
    getSourcePosition: item => item.residence,
    getTargetColor: item => (item.count > 0 ? [65, 188, 155, 220] : [65, 188, 155, 0]),
    getTargetPosition: item => item.coordinates,
    getHeight: 0.5,
    getWidth: item => Math.min(MAX_ARC_WIDTH, item.flowCount * ARC_WIDTH_PER_PERSON),
    greatCircle: true,
    pickable: false,
  }), new ScatterplotLayer({
    id: 'question-2-stay-locations',
    data: [...stayLocations.values()],
    getFillColor: [70, 130, 220, 150],
    getLineColor: [70, 130, 220, 240],
    getLineWidth: 2,
    getPosition: item => item.coordinates,
    getRadius: item => Math.min(MAX_STAY_RADIUS, item.count * STAY_RADIUS_PER_PERSON),
    lineWidthUnits: 'pixels',
    pickable: false,
    radiusMaxPixels: 17,
    radiusMinPixels: 3,
    stroked: true,
  }), new ScatterplotLayer({
    id: 'question-2-outside-destinations',
    data: [...focusedOutsideDestinations.values()],
    getFillColor: [255, 255, 255, 0],
    getLineColor: [255, 255, 255, 235],
    getLineWidth: 1,
    getPosition: item => item.coordinates,
    getRadius: 1100,
    lineWidthUnits: 'pixels',
    pickable: false,
    radiusMaxPixels: 5,
    radiusMinPixels: 2.5,
    stroked: true,
  }), new TextLayer({
    id: 'question-2-flow-symbols',
    data: symbols,
    getColor: item => (item.isStaying
      ? [91, 192, 255, 230]
      : [255, 117, 96, 230]),
    getPosition: item => item.coordinates,
    getSize: item => (item.isStaying ? 13 : 15),
    getText: item => (item.isStaying ? '●' : '◆'),
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'center',
    outlineColor: [20, 31, 46, 220],
    outlineWidth: 2,
    pickable: false,
  }), new TextLayer({
    id: 'question-2-location-labels',
    data: options.showAllData ? [] : labels,
    getColor: [255, 255, 255, 235],
    getPosition: item => [...item.coordinates, LABEL_ELEVATION],
    getSize: 13,
    getText: item => item.name,
    getPixelOffset: [0, -28],
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'bottom',
    outlineColor: [20, 31, 46, 255],
    outlineWidth: 3,
    parameters: { depthTest: false },
    pickable: false,
  })];
}
function normalizeCoordinates(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  const normalized = coordinates.slice(0, 2).map(Number);
  return normalized.every(Number.isFinite) ? normalized : null;
}

function getArcBounds(startCoordinates, endCoordinates) {
  if (!startCoordinates || !endCoordinates) return null;
  const arc = greatCircle(startCoordinates, endCoordinates, { npoints: 64 });
  const [west, south, east, north] = bbox(arc);
  return [[west, south], [east, north]];
}

function Question2(props) {
  const { withInputFields = false, ...visualizationProps } = props;
  const { lastAnswer: contextLastAnswer } = useOutletContext() || {};
  const lastAnswer = props.lastAnswer ?? contextLastAnswer;
  const answer = lastAnswer?.questionId === QUESTION_ID ? lastAnswer.answer : null;
  const focusCoordinates = useMemo(
    () => normalizeCoordinates(answer?.coordinates),
    [answer?.coordinates],
  );
  const startCoordinates = useMemo(
    () => normalizeCoordinates(answer?.startCoordinates || answer?.residenceCoordinates),
    [answer?.residenceCoordinates, answer?.startCoordinates],
  );
  const isOutsideRegion = isOutsideFlandersBrussels(startCoordinates, answer?.residence);
  const focusBounds = useMemo(() => {
    if (isOutsideRegion) return null;

    return getArcBounds(startCoordinates, focusCoordinates);
  }, [focusCoordinates, isOutsideRegion, startCoordinates]);

  return (
    <MapComponent
      questionId={QUESTION_ID}
      buildData={buildQuestionData}
      getLayers={getQuestionLayers}
      {...visualizationProps}
      useParticipantAnswers
      autoRotate
      focusCoordinates={isOutsideRegion ? focusCoordinates : null}
      focusBounds={focusBounds}
      returnToOverview
      revealDataAfterFocus
      legend={(
        <MapLegend
          lang={visualizationProps.tabletLang}
          labelKey="legend-relocation-direction"
          minLabelKey="legend-departing"
          maxLabelKey="legend-arriving"
          circleLabelKey="legend-stays-same-municipality"
          gradient="linear-gradient(90deg, #dc5050, #41bc9b)"
          scaleStyle={{ height: '4px', margin: '5px 0' }}
        />
      )}
    >
      {withInputFields && <Question2Form />}
    </MapComponent>
  );
}
export default Question2;
